import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Play, Pause, RotateCcw, Coffee, Brain, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { label: '25 min', minutes: 25, type: 'focus' },
  { label: '50 min', minutes: 50, type: 'focus' },
  { label: '5 min break', minutes: 5, type: 'break' },
  { label: '15 min break', minutes: 15, type: 'break' },
];

const FocusTimer = ({ isOpen, onClose }) => {
  const [duration, setDuration] = useState(25);
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [sessionType, setSessionType] = useState('focus');
  const [sessionsCompleted, setSessions] = useState(0);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const resetTimer = useCallback((mins = duration) => {
    setTimeLeft(mins * 60);
    setIsRunning(false);
    setIsComplete(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, [duration]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setIsRunning(false);
            setIsComplete(true);
            handleSessionComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // `timeLeft` only gates entry into the interval; subsequent ticks
    // use the functional setTimeLeft(prev => …) updater so the closure
    // doesn't go stale. `handleSessionComplete` is a stable callback
    // hoisted below — including it would cause the timer to be torn
    // down and re-created on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  const handleSessionComplete = useCallback(async () => {
    if (sessionType === 'focus') {
      try {
        await axios.post(`${API}/study-intelligence/focus-session`, {
          duration_minutes: duration,
          type: 'pomodoro',
          subject: 'General',
        });
        setSessions(prev => prev + 1);
        toast.success(`Focus session complete! +${duration * 2} XP earned`);
      } catch { /* analytics call is best-effort */ }
    } else {
      toast.info('Break complete! Ready for another focus session?');
    }
    // Play notification sound
    try { new Audio('/notification.mp3').play().catch(() => {}); } catch { /* ignore audio errors */ }
  }, [sessionType, duration]);

  const toggleTimer = () => {
    if (!isRunning) startTimeRef.current = new Date();
    setIsRunning(!isRunning);
  };

  const selectPreset = (preset) => {
    setDuration(preset.minutes);
    setSessionType(preset.type);
    setTimeLeft(preset.minutes * 60);
    setIsRunning(false);
    setIsComplete(false);
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = 1 - (timeLeft / (duration * 60));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-sm mx-4 rounded-3xl overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="focus-timer-modal"
        >
          {/* Background */}
          <div className={`p-8 ${sessionType === 'focus' ? 'bg-gradient-to-b from-[#0a1628] to-[#1a1040]' : 'bg-gradient-to-b from-[#0a2818] to-[#0f1f2d]'}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                {sessionType === 'focus' ? <Brain className="w-5 h-5 text-[#002FA7]" /> : <Coffee className="w-5 h-5 text-emerald-400" />}
                <span className="text-sm font-semibold text-white/80 capitalize">{sessionType} Session</span>
              </div>
              <div className="flex items-center gap-2">
                {sessionsCompleted > 0 && (
                  <span className="text-xs text-white/40">{sessionsCompleted} done</span>
                )}
                <button aria-label="Close" onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="close-timer-btn">
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>
            </div>

            {/* Timer Circle */}
            <div className="relative w-56 h-56 mx-auto mb-8">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                <circle
                  cx="60" cy="60" r="54" fill="none"
                  stroke={sessionType === 'focus' ? '#002FA7' : '#22c55e'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 54}`}
                  strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress)}`}
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {isComplete ? (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center">
                    <Check className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                    <p className="text-lg font-bold text-white">Done!</p>
                  </motion.div>
                ) : (
                  <>
                    <span className="text-5xl font-mono font-bold text-white tracking-wider" data-testid="timer-display">
                      {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                    </span>
                    <span className="text-xs text-white/30 mt-2">{duration} min {sessionType}</span>
                  </>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button onClick={() => resetTimer()} className="p-3 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors" data-testid="reset-timer-btn">
                <RotateCcw className="w-5 h-5" />
              </button>
              <button aria-label="Pause"
                onClick={toggleTimer}
                className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all ${
                  isRunning ? 'bg-white/20 hover:bg-white/30' : sessionType === 'focus' ? 'bg-[#002FA7] hover:bg-[#002FA7]/80' : 'bg-emerald-500 hover:bg-emerald-600'
                }`}
                data-testid="toggle-timer-btn"
              >
                {isRunning ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="w-11" /> {/* Spacer for symmetry */}
            </div>

            {/* Presets */}
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map(p => (
                <button
                  key={p.label}
                  onClick={() => selectPreset(p)}
                  className={`py-2 px-1 rounded-xl text-xs font-medium transition-all ${
                    duration === p.minutes && sessionType === p.type
                      ? 'bg-white/15 text-white border border-white/20'
                      : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                  }`}
                  data-testid={`preset-${p.minutes}-${p.type}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FocusTimer;
